/**
 * reveal.ts — small helpers for showing the user where a stop went.
 */

/** Minimal stop shape for the confirmation copy. */
interface Named {
  name: string;
}

/**
 * "Between Lakes Entrance and Sydney": the second line of the confirmation
 * after a stop lands. Plain words, no "Stop 3" token: the neighbours are what
 * tell you where it went, and they're the words a one-line toast used to cut.
 */
export function whereItWent(stops: Named[], at: number): string {
  if (at < 0 || at >= stops.length) return '';
  const before = stops[at - 1]?.name;
  const after = stops[at + 1]?.name;
  if (before && after) return `Between ${before} and ${after}`;
  if (before) return `After ${before}, at the end`;
  if (after) return `Before ${after}, at the start`;
  return '';
}

/** Nearest ancestor that actually scrolls vertically, or null for the page. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  let box: HTMLElement | null = el.parentElement;
  while (box && box !== document.body) {
    const oy = getComputedStyle(box).overflowY;
    if (box.scrollHeight > box.clientHeight + 1 && /(auto|scroll)/.test(oy)) return box;
    box = box.parentElement;
  }
  return null;
}

/**
 * Scroll `el` into the middle of its own scroller (never the page around a
 * scrolling panel, so the map beside it doesn't jump). On the page itself,
 * leave room for the sticky planner action bar at the bottom.
 */
export function revealInScroller(el: HTMLElement, bottomReserve?: number): void {
  // Room for whatever sits over the bottom of the list: the planner's action
  // bar (and the confirmation riding on it), plus a fade's worth of air.
  const bar = document.querySelector<HTMLElement>('[data-planner-actionbar], [data-testid="trip-actions"]');
  const reserve = bottomReserve ?? (bar ? bar.getBoundingClientRect().height + 24 : 180);
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth';
  const r = el.getBoundingClientRect();
  const box = scrollParent(el);
  if (box) {
    const b = box.getBoundingClientRect();
    // Inside a scroller the bar sits below it; only the fade overlaps.
    if (r.top >= b.top + 8 && r.bottom <= b.bottom - 32) return; // already in view
    // Centre it, then snap to the nearest row or day heading above, so the
    // list never stops with a row sliced under the sheet's header.
    const at = (y: number) => y - b.top + box.scrollTop;
    const desired = at(r.top) - (b.height - r.height) / 2;
    const anchors = Array.from(box.querySelectorAll<HTMLElement>('[data-testid="trip-stop"], [data-testid="trip-day"], [data-reveal-anchor]')).map(
      (a) => at(a.getBoundingClientRect().top)
    );
    const top = snapScrollTop(desired, anchors, at(r.top), at(r.bottom), b.height);
    box.scrollTo({ top, behavior });
    return;
  }
  const vh = window.innerHeight;
  if (r.top >= 64 && r.bottom <= vh - reserve) return;
  const delta = r.top - (vh - reserve - r.height) / 2;
  window.scrollBy({ top: delta, behavior });
}

/**
 * Where to scroll a list so `el` (content offsets elTop..elBottom) shows in a
 * `viewH`-tall scroller: the centred position `desired`, moved up to the
 * nearest anchor (a row or day heading top, which carry their own padding)
 * at or above it, as long as the element still fits under it. When nothing
 * lets it fit, the element's own top leads. Never below 0.
 */
export function snapScrollTop(desired: number, anchors: number[], elTop: number, elBottom: number, viewH: number): number {
  const fits = (a: number) => a <= elTop && elBottom <= a + viewH - 32;
  const best = anchors
    .filter((a) => a <= desired + 24 && fits(a))
    .sort((x, y) => y - x)[0];
  const top = best !== undefined ? best : elBottom - elTop > viewH - 32 || !fits(desired) ? elTop : desired;
  return Math.max(0, Math.round(top));
}
